import { Output } from "webmidi";

/**
 * MIDI Clock Manager
 *
 * Sends MIDI Clock messages (24 PPQN) to synchronize external hardware
 * like eurorack modules, delays, and other MIDI-enabled devices.
 *
 * MIDI Clock Messages:
 * - 0xF8: Timing Clock (sent 24 times per quarter note)
 * - 0xFA: Start
 * - 0xFB: Continue
 * - 0xFC: Stop
 */
export class MidiClock {
  private bpm: number;
  private isRunning = false;
  private intervalId: number | null = null;
  private clockIntervalMs: number;
  private outputs: Output[] = [];
  private lastClockTime = 0;
  private clocksSent = 0;

  // MIDI Clock constants
  private static readonly PPQN = 24; // Pulses Per Quarter Note
  private static readonly CLOCK_MESSAGE = 0xf8;
  private static readonly START_MESSAGE = 0xfa;
  private static readonly CONTINUE_MESSAGE = 0xfb;
  private static readonly STOP_MESSAGE = 0xfc;

  constructor(bpm: number) {
    this.bpm = bpm;
    this.clockIntervalMs = this.calculateClockInterval(bpm);
  }

  /**
   * Calculate interval between MIDI clock pulses in milliseconds
   * Formula: (60,000ms / BPM) / 24 PPQN
   */
  private calculateClockInterval(bpm: number): number {
    return (60000 / bpm) / MidiClock.PPQN;
  }

  /**
   * Set the tempo (BPM)
   */
  setBPM(bpm: number) {
    this.bpm = bpm;
    this.clockIntervalMs = this.calculateClockInterval(bpm);

    // If running, restart with new tempo
    if (this.isRunning) {
      this.stop();
      this.start();
    }

    console.log(`[MIDI CLOCK] BPM set to ${bpm}, clock interval: ${this.clockIntervalMs.toFixed(2)}ms`);
  }

  /**
   * Add MIDI output to receive clock messages
   */
  addOutput(output: Output) {
    if (!this.outputs.find(o => o.id === output.id)) {
      this.outputs.push(output);
      console.log(`[MIDI CLOCK] Added output: ${output.name}`);
    }
  }

  /**
   * Remove MIDI output
   */
  removeOutput(output: Output) {
    this.outputs = this.outputs.filter(o => o.id !== output.id);
    console.log(`[MIDI CLOCK] Removed output: ${output.name}`);
  }

  /**
   * Clear all outputs
   */
  clearOutputs() {
    this.outputs = [];
    console.log(`[MIDI CLOCK] Cleared all outputs`);
  }

  /**
   * Send a system real-time message to all outputs
   */
  private sendMessage(message: number) {
    this.outputs.forEach(output => {
      try {
        // Send system real-time message (no channel needed)
        output.send([message]);
      } catch (error) {
        console.error(`[MIDI CLOCK] Failed to send message ${message.toString(16)} to ${output.name}:`, error);
      }
    });
  }

  /**
   * Send MIDI Clock pulse (0xF8)
   */
  private sendClock() {
    this.sendMessage(MidiClock.CLOCK_MESSAGE);
    this.clocksSent++;

    const now = performance.now();
    const drift = this.lastClockTime ? now - this.lastClockTime - this.clockIntervalMs : 0;
    this.lastClockTime = now;

    // Log every 24 clocks (1 quarter note) for debugging
    if (this.clocksSent % 24 === 0) {
      console.log(
        `[MIDI CLOCK] Quarter note ${this.clocksSent / 24}, ` +
        `drift: ${drift.toFixed(2)}ms, ` +
        `interval: ${this.clockIntervalMs.toFixed(2)}ms`
      );
    }
  }

  /**
   * Start sending MIDI Clock
   * Sends START message followed by continuous CLOCK messages
   */
  start() {
    if (this.isRunning) {
      console.warn("[MIDI CLOCK] Already running");
      return;
    }

    if (this.outputs.length === 0) {
      console.warn("[MIDI CLOCK] No outputs configured");
      return;
    }

    this.isRunning = true;
    this.clocksSent = 0;
    this.lastClockTime = performance.now();

    // Send START message
    this.sendMessage(MidiClock.START_MESSAGE);
    console.log(`[MIDI CLOCK] Started at ${this.bpm} BPM (${this.clockIntervalMs.toFixed(2)}ms interval)`);

    // Start sending clock pulses
    this.intervalId = window.setInterval(() => {
      this.sendClock();
    }, this.clockIntervalMs);
  }

  /**
   * Continue sending MIDI Clock from current position
   * Sends CONTINUE message followed by CLOCK messages
   */
  continue() {
    if (this.isRunning) {
      console.warn("[MIDI CLOCK] Already running");
      return;
    }

    if (this.outputs.length === 0) {
      console.warn("[MIDI CLOCK] No outputs configured");
      return;
    }

    this.isRunning = true;
    this.lastClockTime = performance.now();

    // Send CONTINUE message
    this.sendMessage(MidiClock.CONTINUE_MESSAGE);
    console.log(`[MIDI CLOCK] Continued at ${this.bpm} BPM`);

    // Resume sending clock pulses
    this.intervalId = window.setInterval(() => {
      this.sendClock();
    }, this.clockIntervalMs);
  }

  /**
   * Stop sending MIDI Clock
   * Sends STOP message and halts clock pulses
   */
  stop() {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;

    // Clear interval
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Send STOP message
    this.sendMessage(MidiClock.STOP_MESSAGE);
    console.log(`[MIDI CLOCK] Stopped after ${this.clocksSent} clocks`);
  }

  /**
   * Check if clock is running
   */
  getIsRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Get current BPM
   */
  getBPM(): number {
    return this.bpm;
  }

  /**
   * Get number of clocks sent
   */
  getClocksSent(): number {
    return this.clocksSent;
  }

  /**
   * Get connected outputs
   */
  getOutputs(): Output[] {
    return [...this.outputs];
  }
}
